import catalog from './fontCatalog.json';

// Site typography (Appearance → Typography), stored as `hm_typography`:
//   { body: <latin id>, heading: <latin id>, arabic: <arabic id> }
//
// SEO / Core Web Vitals: the default (Outfit + Almarai) is exactly what
// public/index.html already loads, so a site that never touches this setting
// ships byte-identical HTML. A custom choice is swapped into the SAME
// non-blocking, display=swap stylesheet by the server (server.js), with only
// the chosen families and the weights they really have. Nothing is ever
// render-blocking and text is always visible while fonts load.

export const LATIN_FONTS = catalog.latin;
export const ARABIC_FONTS = catalog.arabic;
export const DEFAULT_TYPOGRAPHY = Object.freeze({ ...catalog.defaults });

const LINK_ID = 'hm-fonts';
const STYLE_ID = 'hm-typography';
const SANS_FALLBACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const SERIF_FALLBACK = "Georgia, 'Times New Roman', serif";

const byId = (list, id) => list.find(f => f.id === id);

export const getFont = (id) => byId(LATIN_FONTS, id) || byId(ARABIC_FONTS, id) || null;

export function normalizeTypography(raw) {
    const t = raw && typeof raw === 'object' ? raw : {};
    return {
        body: byId(LATIN_FONTS, t.body) ? t.body : DEFAULT_TYPOGRAPHY.body,
        heading: byId(LATIN_FONTS, t.heading) ? t.heading : DEFAULT_TYPOGRAPHY.heading,
        arabic: byId(ARABIC_FONTS, t.arabic) ? t.arabic : DEFAULT_TYPOGRAPHY.arabic,
    };
}

export const isDefaultTypography = (t) => {
    const n = normalizeTypography(t);
    return n.body === DEFAULT_TYPOGRAPHY.body
        && n.heading === DEFAULT_TYPOGRAPHY.heading
        && n.arabic === DEFAULT_TYPOGRAPHY.arabic;
};

// Latin face first, then the Arabic face: Latin fonts have no Arabic glyphs,
// so Arabic text falls through to the Arabic font letter by letter.
export function fontStack(latinId, arabicId) {
    const latin = byId(LATIN_FONTS, latinId) || byId(LATIN_FONTS, DEFAULT_TYPOGRAPHY.body);
    const arabic = byId(ARABIC_FONTS, arabicId) || byId(ARABIC_FONTS, DEFAULT_TYPOGRAPHY.arabic);
    const fallback = latin.style === 'Serif' ? SERIF_FALLBACK : SANS_FALLBACK;
    return `'${latin.family}', '${arabic.family}', ${fallback}`;
}

// One Google Fonts css2 URL for the chosen families. Arabic first so the
// default choice produces exactly the URL public/index.html ships.
export function buildFontsHref(raw) {
    const t = normalizeTypography(raw);
    const ids = [t.arabic, t.body, t.heading].filter((id, i, a) => a.indexOf(id) === i);
    const families = ids.map(getFont).filter(Boolean)
        .map(f => `family=${f.family.replace(/ /g, '+')}:wght@${f.weights.join(';')}`);
    return `https://fonts.googleapis.com/css2?${families.join('&')}&display=swap`;
}

export function typographyCss(raw) {
    const t = normalizeTypography(raw);
    return `:root{--font-sans:${fontStack(t.body, t.arabic)};--font-heading:${fontStack(t.heading, t.arabic)}}`;
}

export function readStoredTypography() {
    try {
        const raw = localStorage.getItem('hm_typography');
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

// Applies a typography choice to the current document. Safe to call often:
// it only touches the DOM when something changed. The server already
// injects the same <link>/<style> for a custom choice, so on a normal page
// load this is a no-op.
export function applyTypography(raw) {
    if (typeof document === 'undefined') return;
    const head = document.head;
    const existingStyle = document.getElementById(STYLE_ID);
    const existingLink = document.getElementById(LINK_ID);

    if (isDefaultTypography(raw)) {
        if (existingStyle) existingStyle.remove();
        if (existingLink) existingLink.remove();
        return;
    }

    const href = buildFontsHref(raw);
    const alreadyLoaded = Array.from(document.querySelectorAll('link[href]'))
        .some(l => l.getAttribute('href') === href && l !== existingLink);
    if (alreadyLoaded) {
        if (existingLink) existingLink.remove();
    } else if (!existingLink || existingLink.getAttribute('href') !== href) {
        const link = existingLink || document.createElement('link');
        link.id = LINK_ID;
        link.rel = 'stylesheet';
        link.href = href;
        if (!existingLink) head.appendChild(link);
    }

    const css = typographyCss(raw);
    if (existingStyle) {
        if (existingStyle.textContent !== css) existingStyle.textContent = css;
    } else {
        const style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent = css;
        head.appendChild(style);
    }
}

// Picker previews: every catalogue family, subset by Google to just the
// characters of the sample text, so the whole list costs a few KB.
// Admin-only; never loaded on the public site.
export function buildPreviewHref(sample) {
    const families = [...LATIN_FONTS, ...ARABIC_FONTS]
        .map(f => `family=${f.family.replace(/ /g, '+')}:wght@400;700`);
    return `https://fonts.googleapis.com/css2?${families.join('&')}&text=${encodeURIComponent(sample)}&display=swap`;
}
